import { addDays, daysBetween, fromKey, startOfDay, toKey } from './dates';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const UNITS = new Set(['day', 'week', 'month', 'year']);

function daysInMonth(year, monthIndex) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function fold(value) {
  return value.replace(/[أإآ]/g, 'ا').replace(/ى/g, 'ي');
}

function rule(pattern, every, unit) {
  return { pattern, every, unit };
}

const RULES = [
  rule(/every\s+other\s+weeks?$/i, 2, 'week'),
  rule(/every\s+other\s+days?$/i, 2, 'day'),
  rule(/every\s+other\s+months?$/i, 2, 'month'),
  rule(/every\s+other\s+years?$/i, 2, 'year'),
  rule(/every\s+day$/i, 1, 'day'),
  rule(/every\s+week$/i, 1, 'week'),
  rule(/every\s+month$/i, 1, 'month'),
  rule(/every\s+year$/i, 1, 'year'),
  rule(/every\s+(\d{1,2})\s+days?$/i, null, 'day'),
  rule(/every\s+(\d{1,2})\s+weeks?$/i, null, 'week'),
  rule(/every\s+(\d{1,2})\s+months?$/i, null, 'month'),
  rule(/every\s+(\d{1,2})\s+years?$/i, null, 'year'),
  rule(/كل\s+يومين$/, 2, 'day'),
  rule(/كل\s+اسبوعين$/, 2, 'week'),
  rule(/كل\s+شهرين$/, 2, 'month'),
  rule(/كل\s+سنتين$/, 2, 'year'),
  rule(/كل\s+يوم$/, 1, 'day'),
  rule(/كل\s+اسبوع$/, 1, 'week'),
  rule(/كل\s+شهر$/, 1, 'month'),
  rule(/كل\s+(?:سنه|عام)$/, 1, 'year'),
  rule(/كل\s+(\d{1,2})\s+(?:ايام|يوم)$/, null, 'day'),
  rule(/كل\s+(\d{1,2})\s+(?:اسابيع|اسبوع)$/, null, 'week'),
  rule(/كل\s+(\d{1,2})\s+(?:اشهر|شهر)$/, null, 'month'),
  rule(/كل\s+(\d{1,2})\s+(?:سنوات|سنين|سنه|عام)$/, null, 'year'),
];

export function parseRecurrence(raw) {
  const text = String(raw || '')
    .trim()
    .replace(/[.。]+$/g, '')
    .trim();
  if (!text) return null;
  const folded = fold(text);
  for (const item of RULES) {
    const match = folded.match(item.pattern);
    if (!match) continue;
    const every = item.every == null ? Number(match[1]) : item.every;
    if (!Number.isInteger(every) || every < 1 || every > 99) return null;
    const body = text.slice(0, match.index).trim();
    if (!body) return null;
    return { text: body, every, unit: item.unit };
  }
  return null;
}

export function occursOn(item, dateKey) {
  if (!item || !UNITS.has(item.unit) || !DAY.test(item.start) || !DAY.test(dateKey)) return false;
  if (dateKey < item.start) return false;
  const every = Math.min(99, Math.max(1, Math.round(Number(item.every) || 1)));
  const start = fromKey(item.start);
  const date = fromKey(dateKey);
  if (Number.isNaN(start.getTime()) || Number.isNaN(date.getTime())) return false;

  if (item.unit === 'day') return daysBetween(start, date) % every === 0;
  if (item.unit === 'week') return daysBetween(start, date) % (every * 7) === 0;

  if (item.unit === 'month') {
    const months =
      (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
    if (months < 0 || months % every !== 0) return false;
    const dim = daysInMonth(date.getFullYear(), date.getMonth());
    return date.getDate() === Math.min(start.getDate(), dim);
  }

  const years = date.getFullYear() - start.getFullYear();
  if (years < 0 || years % every !== 0) return false;
  if (date.getMonth() !== start.getMonth()) return false;
  const dim = daysInMonth(date.getFullYear(), date.getMonth());
  return date.getDate() === Math.min(start.getDate(), dim);
}

export function nextOccurrence(item, fromDate) {
  const from = toKey(startOfDay(fromDate));
  if (!item || !DAY.test(item.start)) return from;
  if (item.start >= from) return item.start;
  const every = Math.min(99, Math.max(1, Math.round(Number(item.every) || 1)));
  const start = fromKey(item.start);

  if (item.unit === 'day' || item.unit === 'week') {
    const step = item.unit === 'week' ? every * 7 : every;
    const delta = daysBetween(start, fromKey(from));
    const mod = ((delta % step) + step) % step;
    return toKey(addDays(fromKey(from), mod === 0 ? 0 : step - mod));
  }

  if (item.unit === 'month') {
    let year = start.getFullYear();
    let month = start.getMonth();
    for (let i = 0; i < 2400; i += 1) {
      const day = Math.min(start.getDate(), daysInMonth(year, month));
      const key = toKey(new Date(year, month, day));
      if (key >= from) return key;
      month += every;
      year += Math.floor(month / 12);
      month %= 12;
    }
  }

  for (let i = 0; i < 200; i += 1) {
    const year = start.getFullYear() + i * every;
    const day = Math.min(start.getDate(), daysInMonth(year, start.getMonth()));
    const key = toKey(new Date(year, start.getMonth(), day));
    if (key >= from) return key;
  }
  return item.start;
}

export function recurrenceLabel(item, t) {
  const every = Math.min(99, Math.max(1, Math.round(Number(item.every) || 1)));
  const unit = UNITS.has(item.unit) ? item.unit : 'day';
  if (every === 2 && unit === 'week') return t('recur.everyOtherWeek');
  if (every === 2 && unit === 'day') return t('recur.everyOtherDay');
  if (every === 1) return t(`recur.every.${unit}`);
  return t(`recur.everyN.${unit}`, { n: every });
}
